import { ChangeDetectionStrategy, Component, ViewChild } from '@angular/core';
import { MatLegacyMenuTrigger as MatMenuTrigger } from '@angular/material/legacy-menu';
import { UntilDestroy } from '@ngneat/until-destroy';
import { connectState, getTrackByFn } from '@y42/angular/util';
import { DataTest, SchemaTestForm } from '@y42/data-test';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { map } from 'rxjs/operators';
import { DataTestColumnsCellRendererUIModel } from './data-test-columns-cell-renderer.model';
import { DataTestColumnsExtractorService } from './data-test-columns-extractor.service';

export interface DataTestColumnsCellRendererParam {
  usePopupForColumnNames?: boolean;
}

export interface DataTestColumnsCellRendererInputValue {
  dataTest: DataTest | null;
  schemaTest: SchemaTestForm | null;
}

type CellRendererParamsAndValue = ICellRendererParams<unknown, DataTestColumnsCellRendererInputValue> &
  DataTestColumnsCellRendererParam;

@UntilDestroy()
@Component({
  selector: 'y42-data-test-columns-cell-renderer',
  templateUrl: './data-test-columns-cell-renderer.component.html',
  styleUrls: ['./data-test-columns-cell-renderer.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [DataTestColumnsExtractorService],
})
export class DataTestColumnsCellRendererComponent implements ICellRendererAngularComp {
  constructor(private readonly dataTestColumnsExtractorService: DataTestColumnsExtractorService) {}

  @ViewChild('matMenuTrigger') editorMenuTrigger!: MatMenuTrigger;

  private readonly columnsToShow = 3;
  private readonly value$$ = new BehaviorSubject<DataTestColumnsCellRendererInputValue | null>(null);
  private readonly params$$ = new BehaviorSubject<DataTestColumnsCellRendererParam | null>(null);
  private readonly isGroup$$ = new BehaviorSubject<boolean>(false);
  private readonly extractedColumns$ = this.dataTestColumnsExtractorService.getColumns(
    this.value$$.pipe(map((x) => x?.dataTest)),
    this.value$$.pipe(map((x) => x?.schemaTest)),
  );

  readonly state = connectState(this, {
    allColumns: this.extractedColumns$,
    columns: this.extractedColumns$.pipe(map((columns) => columns.slice(0, this.columnsToShow))),
    showThreeDots: this.extractedColumns$.pipe(map((columns) => columns.length > this.columnsToShow)),
    threeDotsHoverText: this.extractedColumns$.pipe(map((columns) => columns.map((x) => x.columnName).join(', '))),
    expandToSee: this.isGroup$$,
    showPopupButtonInsteadOfColumnNames: combineLatest([this.params$$, this.extractedColumns$]).pipe(
      map(([params, extractedColumns]) => (params?.usePopupForColumnNames ?? false) && extractedColumns.length > 1),
    ),
  });

  readonly trackByFn = getTrackByFn<DataTestColumnsCellRendererUIModel>('columnName');

  readonly columnsSearchCallback = (item: DataTestColumnsCellRendererUIModel, searchQuery: string): boolean => {
    return item.columnName.toLowerCase().includes(searchQuery);
  };

  close() {
    this.editorMenuTrigger.closeMenu();
  }

  agInit(params: CellRendererParamsAndValue): void {
    this.refreshData(params);
  }

  refresh(params: CellRendererParamsAndValue): boolean {
    this.refreshData(params);
    return true;
  }

  private refreshData(params: CellRendererParamsAndValue): void {
    this.value$$.next(params.value ?? null);
    this.params$$.next({
      usePopupForColumnNames: params.usePopupForColumnNames,
    });
    this.isGroup$$.next(params.node.group ?? false);
  }
}
